'use client';

import React from 'react';
import Link from 'next/link';
import dayjs from 'dayjs';
import { numberShorthand } from '@benzinga/utils';
import type { GovernmentMember, SecurityTrader } from '@benzinga/gov-trades';

import { memberDisplayNameToProfileSlug } from '../../../../src/components/GovTrades/memberProfileSlug';
import { currentMemberLabelFromStatus, getFullStateName, getPoliticalPartyName } from './utils';

type Variant = 'volume' | 'gainer' | 'loser';

type TopVolumeLeaderCardProps =
  | { variant?: 'volume'; member: GovernmentMember }
  | { variant: 'gainer' | 'loser'; trader: SecurityTrader };

const VARIANT_COPY: Record<Variant, { eyebrow: string; footnote: string }> = {
  volume: {
    eyebrow: 'TOP VOLUME LEADER',
    footnote: 'Highest disclosed trade volume in the tracker period',
  },
  gainer: {
    eyebrow: 'TOP GAINER',
    footnote: 'Best 12-month return performance',
  },
  loser: {
    eyebrow: 'TOP LOSER',
    footnote: 'Worst 12-month return performance',
  },
};

type NormalizedCardData = {
  displayName: string;
  headshot?: string;
  party?: string;
  chamber?: string;
  state?: string;
  status?: string;
  volume?: number;
  totalTrades?: number;
  lastTraded?: string;
  returnPercent?: number;
};

const normalizeFromMember = (member: GovernmentMember): NormalizedCardData => ({
  displayName: member.filer_info?.display_name,
  headshot: member.filer_info?.headshot,
  party: member.filer_info?.party,
  chamber: member.filer_info?.chamber,
  state: member.filer_info?.state,
  status: member.filer_info?.status,
  volume: member.stats?.total_volume_of_trades,
  totalTrades: member.stats?.number_of_trades,
  lastTraded: member.stats?.date_of_last_recorded_trade,
});

const normalizeFromTrader = (trader: SecurityTrader): NormalizedCardData => ({
  displayName: trader.display_name,
  headshot: trader.headshot,
  party: trader.party,
  chamber: trader.chamber,
  state: trader.state,
  volume: trader.ttm_total_volume,
  totalTrades: trader.total_trades,
  returnPercent: trader.ttm_return,
});

export const TopVolumeLeaderCard = (props: TopVolumeLeaderCardProps) => {
  const variant: Variant = props.variant ?? 'volume';
  const data: NormalizedCardData =
    'member' in props ? normalizeFromMember(props.member) : normalizeFromTrader(props.trader);

  const slug = memberDisplayNameToProfileSlug(data.displayName);
  const href = `/government-trades/members/${slug}`;

  const subtitle = [getPoliticalPartyName(data.party ?? ''), data.chamber, getFullStateName(data.state ?? '')]
    .map(part => (typeof part === 'string' ? part.trim() : part))
    .filter((part): part is string => Boolean(part) && part !== '-')
    .join(' / ');

  const currentMemberLabel = variant === 'volume' ? currentMemberLabelFromStatus(data.status) : 'yes';
  const { eyebrow, footnote } = VARIANT_COPY[variant];

  const showReturn = variant !== 'volume' && typeof data.returnPercent === 'number';
  const returnPct = (data.returnPercent ?? 0) * 100;
  const returnIsPositive = returnPct >= 0;

  return (
    <div className="top-volume-leader-card key-stats key-stats--tile w-full">
      <div className="top-volume-leader-card__inner key-stats--tile-inner">
        <h3 className="key-stats__eyebrow">{eyebrow}</h3>

        <Link className="top-volume-leader-card__profile" href={href}>
          <div className="top-volume-leader-card__avatar-wrap">
            <img
              alt={data.displayName}
              className="top-volume-leader-card__avatar"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = '/next-assets/images/no_picture.png';
              }}
              src={data.headshot}
            />
          </div>
          <div className="top-volume-leader-card__profile-text">
            <p className="top-volume-leader-card__name">{data.displayName}</p>
            <p className="key-stats__sub capitalize">{subtitle}</p>
          </div>
        </Link>

        <div className="key-stats__grid2 top-volume-leader-card__metrics" aria-label="Trading activity">
          {variant === 'volume' && (
            <div className="key-stats__cell">
              <span className="key-stats__cell-label">Trade volume</span>
              <span className="key-stats__cell-value">
                {typeof data.volume === 'number' ? `$${numberShorthand(data.volume, 1)}` : '—'}
              </span>
            </div>
          )}
          <div className="key-stats__cell">
            <span className="key-stats__cell-label">Total trades</span>
            <span className="key-stats__cell-value">
              {typeof data.totalTrades === 'number' ? data.totalTrades.toLocaleString() : '—'}
            </span>
          </div>
          <div className="key-stats__cell">
            <span className="key-stats__cell-label">Chamber</span>
            <span className="key-stats__cell-value">{data.chamber || '—'}</span>
          </div>
          {showReturn ? (
            <div className="key-stats__cell">
              <span className="key-stats__cell-label">Return</span>
              <span
                className={
                  'key-stats__cell-value' +
                  (returnIsPositive
                    ? ' top-volume-leader-card__value--positive'
                    : ' top-volume-leader-card__value--negative')
                }
              >
                {returnIsPositive ? '+' : ''}
                {returnPct.toFixed(2)}%
              </span>
            </div>
          ) : (
            <div className="key-stats__cell">
              <span className="key-stats__cell-label">Last traded</span>
              <span className="key-stats__cell-value">
                {data.lastTraded ? dayjs(data.lastTraded).format('MMM D, YYYY') : '—'}
              </span>
            </div>
          )}
        </div>

        <div className="top-volume-leader-card__detail-block">
          <div className="key-stats__line">
            <span className="key-stats__line-label">Current member</span>
            <span
              className={
                'key-stats__line-value capitalize' +
                (currentMemberLabel === 'yes' ? ' top-volume-leader-card__value--positive' : '')
              }
            >
              {currentMemberLabel}
            </span>
          </div>

          <div className="key-stats__line">
            <span className="key-stats__line-label">Party</span>
            <span className="key-stats__line-value">{getPoliticalPartyName(data.party ?? '')}</span>
          </div>
          <div className="key-stats__line">
            <span className="key-stats__line-label">State</span>
            <span className="key-stats__line-value capitalize">{getFullStateName(data.state ?? '')}</span>
          </div>
        </div>

        <div className="top-volume-leader-card__footer">
          <p className="key-stats__footnote">{footnote}</p>
          <Link className="top-volume-leader-card__cta" href={href}>
            View profile
          </Link>
        </div>
      </div>
    </div>
  );
};
